// API Configuration
export const API_CONFIG = {
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000',
  timeout: 30000,
  /**
   * Multipart uploads get their own budget: a 15-image OCR chunk is several MB
   * on the wire AND writes every page to object storage before responding, so
   * the 30s JSON budget aborted it mid-flight while the backend kept going.
   */
  uploadTimeout: 600000,
  retries: 3,
  retryDelay: 1000
} as const;

const API_PREFIX = '/api/v1';

export const API_ENDPOINTS = {
  HEALTH: '/api/health',
  AUTH: {
    LOGIN: `${API_PREFIX}/auth/login`,
    VERIFY_LOGIN_OTP: `${API_PREFIX}/auth/verify-login-otp`,
    FORGOT_PASSWORD: `${API_PREFIX}/auth/forgot-password`,
    RESET_PASSWORD: `${API_PREFIX}/auth/reset-password`,
    PERMISSIONS: `${API_PREFIX}/auth/permissions`,
    RESEND_OTP: `${API_PREFIX}/auth/resend-login-otp`,
    LOGOUT: `${API_PREFIX}/auth/logout`,
    REFRESH: `${API_PREFIX}/auth/refresh`
  },
  PRODUCTS: {
    LIST: '/products',
    GET: (id: number) => `/products/${id}`,
    CREATE: '/products',
    UPDATE: (id: number) => `/products/${id}`,
    DELETE: (id: number) => `/products/${id}`
  },
  KANBAN: {
    TASKS: '/kanban/tasks',
    COLUMNS: '/kanban/columns'
  },
  OVERVIEW: {
    STATS: '/overview/stats',
    RECENT_SALES: '/overview/recent-sales'
  },
  ROLES: {
    LIST: `${API_PREFIX}/roles`,
    GET: (id: number) => `${API_PREFIX}/roles/${id}`,
    CREATE: `${API_PREFIX}/roles`,
    UPDATE: (id: number) => `${API_PREFIX}/roles/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/roles/${id}`,
    METRICS: `${API_PREFIX}/roles/metrics`,
    DUPLICATE: (id: number) => `${API_PREFIX}/roles/${id}/duplicate`,
    BULK_DELETE: `${API_PREFIX}/roles/bulk-delete`,
    BULK_ASSIGN_PERMISSIONS: `${API_PREFIX}/roles/bulk-assign-permissions`
  },
  USERS: {
    LIST: `${API_PREFIX}/users`,
    GET: (id: number) => `${API_PREFIX}/users/${id}`,
    CREATE: `${API_PREFIX}/users`,
    UPDATE: (id: number) => `${API_PREFIX}/users/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/users/${id}`,
    ASSIGN_ROLE: (userId: number) => `${API_PREFIX}/users/${userId}/roles`,
    REMOVE_ROLE: (userId: number) => `${API_PREFIX}/users/${userId}/roles`,
    METRICS: `${API_PREFIX}/users/metrics`,
    DEACTIVATE: (userId: number) => `${API_PREFIX}/users/${userId}/deactivate`,
    UNLOCK: (userId: number) => `${API_PREFIX}/users/${userId}/unlock`,
    ACTIVATE: (userId: number) => `${API_PREFIX}/users/${userId}/activate`,
    BULK_DELETE: `${API_PREFIX}/users/bulk-delete`,
    BULK_ACTIVATE: `${API_PREFIX}/users/bulk-activate`,
    BULK_DEACTIVATE: `${API_PREFIX}/users/bulk-deactivate`,
    BULK_IMPORT: `${API_PREFIX}/users/bulk-import`,
    IMPORT_TEMPLATE: `${API_PREFIX}/users/import-template`,
    DRIVER_AVAILABILITY: (userId: number) =>
      `${API_PREFIX}/users/${userId}/driver-availability`,
    OPERATOR_AVAILABILITY: (userId: number) =>
      `${API_PREFIX}/users/${userId}/operator-availability`,
    BY_PERMISSION: (permission: string) =>
      `${API_PREFIX}/users?permission=${encodeURIComponent(permission)}`
  },
  CONTAINER_TYPES: {
    LIST: `${API_PREFIX}/container-types`,
    GET: (id: number) => `${API_PREFIX}/container-types/${id}`,
    CREATE: `${API_PREFIX}/container-types`,
    UPDATE: (id: number) => `${API_PREFIX}/container-types/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/container-types/${id}`,
    BULK_DELETE: `${API_PREFIX}/container-types/bulk-delete`,
    BULK_CHANGE_STATUS: `${API_PREFIX}/container-types/bulk-change-status`
  },
  SUB_CONTAINER_TYPES: {
    LIST: `${API_PREFIX}/sub-container-types`,
    GET: (id: number) => `${API_PREFIX}/sub-container-types/${id}`,
    CREATE: `${API_PREFIX}/sub-container-types`,
    UPDATE: (id: number) => `${API_PREFIX}/sub-container-types/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/sub-container-types/${id}`,
    BULK_DELETE: `${API_PREFIX}/sub-container-types/bulk-delete`
  },
  DYNAMIC_FORMS: {
    LIST: `${API_PREFIX}/dynamic-forms`,
    GET: (id: number) => `${API_PREFIX}/dynamic-forms/${id}`,
    CREATE: `${API_PREFIX}/dynamic-forms`,
    UPDATE: (id: number) => `${API_PREFIX}/dynamic-forms/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/dynamic-forms/${id}`,
    SYNC_FIELDS: (id: number) => `${API_PREFIX}/dynamic-forms/${id}/fields`,
    ACTIVATE: (id: number) => `${API_PREFIX}/dynamic-forms/${id}/activate`,
    DEACTIVATE: (id: number) => `${API_PREFIX}/dynamic-forms/${id}/deactivate`,
    SET_DEFAULT: (id: number) =>
      `${API_PREFIX}/dynamic-forms/${id}/set-default`,
    UNSET_DEFAULT: (id: number) =>
      `${API_PREFIX}/dynamic-forms/${id}/unset-default`,
    DUPLICATE: (id: number) => `${API_PREFIX}/dynamic-forms/${id}/duplicate`,
    ACTIVE_BY_TYPE: (type: string) =>
      `${API_PREFIX}/dynamic-forms/active/${type}`
  },
  REGISTRATIONS: {
    LIST: `${API_PREFIX}/registrations`,
    GET: (id: number) => `${API_PREFIX}/registrations/${id}`,
    CREATE: `${API_PREFIX}/registrations`,
    UPDATE: (id: number) => `${API_PREFIX}/registrations/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/registrations/${id}`,
    METRICS: `${API_PREFIX}/registrations/metrics`,
    BULK_DELETE: `${API_PREFIX}/registrations/bulk-delete`,
    ACTIVITIES: (id: number) => `${API_PREFIX}/registrations/${id}/activities`,
    ADVANCE_STAGE: (id: number) =>
      `${API_PREFIX}/registrations/${id}/advance-stage`,
    SET_PAGE_COUNT: (id: number) =>
      `${API_PREFIX}/registrations/${id}/page-count`,
    RETURN_TO_REGISTRATION: (id: number) =>
      `${API_PREFIX}/registrations/${id}/return-to-registration`,
    STICKING_REQUEST_EXTRA_LABELS: (id: number) =>
      `${API_PREFIX}/registrations/${id}/sticking/request-extra-labels`,
    STICKING_ADJUST_ACTUAL_COUNT: (id: number) =>
      `${API_PREFIX}/registrations/${id}/sticking/adjust-actual-count`
  },
  PROFILE: {
    GET: `${API_PREFIX}/profile`,
    UPDATE: `${API_PREFIX}/profile`,
    CHANGE_PASSWORD: `${API_PREFIX}/profile/change-password`
  },
  APP_SETTINGS: {
    LIST: `${API_PREFIX}/app-settings`,
    UPDATE: `${API_PREFIX}/app-settings`
  },
  WORKFLOW: {
    STAGES: `${API_PREFIX}/workflow/stages`
  },
  CONFIGURATIONS: {
    LIST: `${API_PREFIX}/configurations`,
    UPDATE: `${API_PREFIX}/configurations`,
    SCAN_PAGE_QUOTA: `${API_PREFIX}/configurations/scan-page-quota`
  },
  AI_MODELS: {
    LIST: `${API_PREFIX}/ai-models`,
    GET: (service: string) => `${API_PREFIX}/ai-models/${service}`,
    UPDATE: (service: string) => `${API_PREFIX}/ai-models/${service}`,
    TEST: (service: string) => `${API_PREFIX}/ai-models/${service}/test`,
    ACTIVATE: (service: string) =>
      `${API_PREFIX}/ai-models/${service}/activate`,
    DEACTIVATE: (service: string) =>
      `${API_PREFIX}/ai-models/${service}/deactivate`,
    REMOVE_KEY: (service: string) => `${API_PREFIX}/ai-models/${service}/key`
  },
  LICENSE: {
    STATUS: `${API_PREFIX}/license`,
    ACTIVATE: `${API_PREFIX}/license/activate`
  },
  REJECTION_REASONS: {
    LIST: `${API_PREFIX}/rejection-reasons`,
    UPDATE: `${API_PREFIX}/rejection-reasons`
  },
  LABS: {
    LIST: `${API_PREFIX}/labs`,
    UPDATE: `${API_PREFIX}/labs`
  },
  PERMISSIONS: {
    LIST: `${API_PREFIX}/permissions`
  },
  NATIONALITIES: {
    LIST: `${API_PREFIX}/nationalities`
  },
  STORAGE_LOCATIONS: {
    LIST: `${API_PREFIX}/storage-locations`,
    GET: (id: number) => `${API_PREFIX}/storage-locations/${id}`,
    CREATE: `${API_PREFIX}/storage-locations`,
    UPDATE: (id: number) => `${API_PREFIX}/storage-locations/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/storage-locations/${id}`,
    STATS: `${API_PREFIX}/storage-locations/stats`,
    CHECK_CODE: `${API_PREFIX}/storage-locations/check-code`
  },
  AUDIT_LOGS: {
    LIST: `${API_PREFIX}/audit-logs`,
    LATEST: `${API_PREFIX}/audit-logs/latest`,
    FILTERS: `${API_PREFIX}/audit-logs/filters`,
    CREATE: `${API_PREFIX}/audit-logs`
  },
  LOCATIONS: {
    LIST: `${API_PREFIX}/locations`,
    GET: (id: number) => `${API_PREFIX}/locations/${id}`,
    CREATE: `${API_PREFIX}/locations`,
    UPDATE: (id: number) => `${API_PREFIX}/locations/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/locations/${id}`,
    CHECK_CODE: `${API_PREFIX}/locations/check-code`
  },
  INDEXING: {
    METRICS: `${API_PREFIX}/sticking/metrics`,
    PAGE_INDEXES: (regId: number) =>
      `${API_PREFIX}/registrations/${regId}/page-indexes`,
    PAGE_INDEX: (regId: number, piId: number) =>
      `${API_PREFIX}/registrations/${regId}/page-indexes/${piId}`,
    BULK_DELETE_PAGE_INDEXES: (regId: number) =>
      `${API_PREFIX}/registrations/${regId}/page-indexes/bulk-delete`,
    PRINT_BATCHES: (regId: number) =>
      `${API_PREFIX}/registrations/${regId}/print-batches`,
    PRINT_BATCH: (regId: number, batchId: number) =>
      `${API_PREFIX}/registrations/${regId}/print-batches/${batchId}`,
    PRINT_BATCH_METRICS: (regId: number) =>
      `${API_PREFIX}/registrations/${regId}/print-batches/metrics`
  },
  BOX_TYPES: {
    LIST: `${API_PREFIX}/box-types`,
    GET: (id: number) => `${API_PREFIX}/box-types/${id}`,
    CREATE: `${API_PREFIX}/box-types`,
    UPDATE: (id: number) => `${API_PREFIX}/box-types/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/box-types/${id}`,
    BULK_DELETE: `${API_PREFIX}/box-types/bulk-delete`,
    BULK_CHANGE_STATUS: `${API_PREFIX}/box-types/bulk-change-status`
  },
  BOXES: {
    LIST: `${API_PREFIX}/boxes`,
    GET: (id: number) => `${API_PREFIX}/boxes/${id}`,
    CREATE: `${API_PREFIX}/boxes`,
    UPDATE: (id: number) => `${API_PREFIX}/boxes/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/boxes/${id}`,
    METRICS: `${API_PREFIX}/boxes/metrics`,
    BULK_DELETE: `${API_PREFIX}/boxes/bulk-delete`,
    ADD_REGISTRATION: (boxId: number) =>
      `${API_PREFIX}/boxes/${boxId}/registrations`,
    REMOVE_REGISTRATION: (boxId: number, registrationId: number) =>
      `${API_PREFIX}/boxes/${boxId}/registrations/${registrationId}`,
    UPDATE_STATUS: (id: number) => `${API_PREFIX}/boxes/${id}/status`,
    OPEN: (id: number) => `${API_PREFIX}/boxes/${id}/open`,
    CLOSE: (id: number) => `${API_PREFIX}/boxes/${id}/close`,
    ROLLBACK: (id: number) => `${API_PREFIX}/boxes/${id}/rollback`
  },
  REVIEW: {
    METRICS: `${API_PREFIX}/review/metrics`,
    REJECT_STAGE: (id: number) =>
      `${API_PREFIX}/registrations/${id}/reject-stage`
  },
  SHIPMENTS: {
    LIST: `${API_PREFIX}/shipments`,
    GET: (id: number) => `${API_PREFIX}/shipments/${id}`,
    CREATE: `${API_PREFIX}/shipments`,
    UPDATE: (id: number) => `${API_PREFIX}/shipments/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/shipments/${id}`,
    CHANGE_STATUS: (id: number) =>
      `${API_PREFIX}/shipments/${id}/change-status`,
    ASSIGN: (id: number) => `${API_PREFIX}/shipments/${id}/assign`,
    CONFIRM: (id: number) => `${API_PREFIX}/shipments/${id}/confirm`,
    DISPATCH: (id: number) => `${API_PREFIX}/shipments/${id}/dispatch`,
    SAVE_DRAFT: `${API_PREFIX}/shipments/save-draft`,
    METRICS: `${API_PREFIX}/shipments/metrics`,
    BULK_DELETE: `${API_PREFIX}/shipments/bulk-delete`,
    BULK_CHANGE_STATUS: `${API_PREFIX}/shipments/bulk-change-status`
  },
  DEVICES: {
    LIST: `${API_PREFIX}/devices`,
    GET: (id: number) => `${API_PREFIX}/devices/${id}`,
    CREATE: `${API_PREFIX}/devices`,
    UPDATE: (id: number) => `${API_PREFIX}/devices/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/devices/${id}`,
    METRICS: `${API_PREFIX}/devices/metrics`,
    BULK_DELETE: `${API_PREFIX}/devices/bulk-delete`
  },
  NOTIFICATIONS: {
    LIST: `${API_PREFIX}/notifications`,
    UNREAD_COUNT: `${API_PREFIX}/notifications/unread-count`,
    MARK_AS_READ: (id: string) => `${API_PREFIX}/notifications/${id}/read`,
    MARK_ALL_READ: `${API_PREFIX}/notifications/mark-all-read`
  },
  HANDOVER_SCANNING: {
    LIST: `${API_PREFIX}/handover-scanning`,
    SHOW: (id: number) => `${API_PREFIX}/handover-scanning/${id}`,
    CREATE: `${API_PREFIX}/handover-scanning`,
    SELF_ASSIGN: `${API_PREFIX}/handover-scanning/self-assign`,
    UPDATE: (id: number) => `${API_PREFIX}/handover-scanning/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/handover-scanning/${id}`,
    METRICS: `${API_PREFIX}/handover-scanning/metrics`,
    BULK_DELETE: `${API_PREFIX}/handover-scanning/bulk-delete`,
    RECEIVING_LIST: `${API_PREFIX}/handover-scanning/receiving-list`
  },
  RECEPTION: {
    SHIPMENTS: `${API_PREFIX}/reception/shipments`,
    SHIPMENT: (id: number) => `${API_PREFIX}/reception/shipments/${id}`,
    METRICS: `${API_PREFIX}/reception/shipments/metrics`,
    BULK_DELETE: `${API_PREFIX}/reception/shipments/bulk-delete`,
    VERIFY_QR: (shipmentId: number) =>
      `${API_PREFIX}/reception/shipments/${shipmentId}/verify-qr`,
    VERIFY_QR_BY_NUMBER: `${API_PREFIX}/reception/shipments/verify-qr-by-number`,
    VERIFY_COUNT: (sessionId: number) =>
      `${API_PREFIX}/reception/sessions/${sessionId}/verify-count`,
    REPORT_DISCREPANCY: (sessionId: number) =>
      `${API_PREFIX}/reception/sessions/${sessionId}/report-discrepancy`,
    VERIFY_ITEM: (sessionId: number) =>
      `${API_PREFIX}/reception/sessions/${sessionId}/verify-item`,
    SUMMARY: (sessionId: number) =>
      `${API_PREFIX}/reception/sessions/${sessionId}/summary`,
    COMPLETE: (sessionId: number) =>
      `${API_PREFIX}/reception/sessions/${sessionId}/complete`,
    FILTER_OPTIONS: `${API_PREFIX}/reception/shipments/filter-options`
  },
  SORTING: {
    SESSIONS: `${API_PREFIX}/sorting/sessions`,
    SESSION: (id: number) => `${API_PREFIX}/sorting/sessions/${id}`,
    METRICS: `${API_PREFIX}/sorting/sessions/metrics`,
    BULK_DELETE: `${API_PREFIX}/sorting/sessions/bulk-delete`,
    START: (id: number) => `${API_PREFIX}/sorting/sessions/${id}/start`,
    SCAN: (id: number) => `${API_PREFIX}/sorting/sessions/${id}/scan`,
    SCAN_PAGE: (id: number) => `${API_PREFIX}/sorting/sessions/${id}/scan-page`,
    REFUSE: (id: number) => `${API_PREFIX}/sorting/sessions/${id}/refuse`,
    COMPLETE_CONTAINER: (id: number) =>
      `${API_PREFIX}/sorting/sessions/${id}/complete-container`,
    COMPLETE: (id: number) => `${API_PREFIX}/sorting/sessions/${id}/complete`
  },
  PHOTO_SCANNING: {
    LIST: `${API_PREFIX}/photo-scanning/batches`,
    GET: (id: number) => `${API_PREFIX}/photo-scanning/batches/${id}`,
    METRICS: `${API_PREFIX}/photo-scanning/metrics`,
    START: (id: number) => `${API_PREFIX}/photo-scanning/batches/${id}/start`,
    REFUSE: (registrationId: number) =>
      `${API_PREFIX}/photo-scanning/${registrationId}/refuse`,
    SCAN_PAGE: (id: number) =>
      `${API_PREFIX}/photo-scanning/batches/${id}/scan`,
    COMPLETE: (id: number) =>
      `${API_PREFIX}/photo-scanning/batches/${id}/complete`,
    DELETE: (id: number) => `${API_PREFIX}/photo-scanning/batches/${id}`,
    BULK_DELETE: `${API_PREFIX}/photo-scanning/batches/bulk-delete`,
    SCAN_FROM_CAMERA: (id: number) =>
      `${API_PREFIX}/photo-scanning/batches/${id}/scan-from-camera`,
    SCAN_FROM_IMAGES: (id: number) =>
      `${API_PREFIX}/photo-scanning/batches/${id}/scan-from-images`,
    PAGE_IMAGE: (batchId: number, pageId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pages/${pageId}/image`,
    PAGE_THUMBNAIL: (batchId: number, pageId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pages/${pageId}/thumbnail`,
    ROTATE_PAGE: (batchId: number, pageId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pages/${pageId}/rotate`,
    ENHANCE_PAGE: (batchId: number, pageId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pages/${pageId}/enhance`,
    UPDATE_QR_BBOX: (batchId: number, pageId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pages/${pageId}/qr-bbox`,
    DELETE_PAGE: (batchId: number, pageId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pages/${pageId}`,
    REORDER_PAGES: (batchId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pages/reorder`,
    BATCH_PDF: (batchId: number) =>
      `${API_PREFIX}/photo-scanning/batches/${batchId}/pdf`,
    TRANSFER_TO_QA: (id: number) =>
      `${API_PREFIX}/photo-scanning/batches/${id}/transfer-to-qa`
  },
  QR_REVIEW: {
    LIST: `${API_PREFIX}/qr-review/batches`,
    METRICS: `${API_PREFIX}/qr-review/metrics`,
    SHOW: (id: number) => `${API_PREFIX}/qr-review/batches/${id}`,
    START_REVIEW: (id: number) =>
      `${API_PREFIX}/qr-review/batches/${id}/start-review`,
    END_SESSION: (id: number) =>
      `${API_PREFIX}/qr-review/batches/${id}/end-session`,
    SUBMIT_VERDICT: (batchId: number, pageId: number) =>
      `${API_PREFIX}/qr-review/batches/${batchId}/pages/${pageId}/verdict`,
    REJECT_SESSION: (id: number) =>
      `${API_PREFIX}/qr-review/batches/${id}/reject-session`,
    UPDATE_PAGE_QR_SCAN: (batchId: number, pageId: number) =>
      `${API_PREFIX}/qr-review/batches/${batchId}/pages/${pageId}/qr-scan`
  },
  REPAIRS: {
    LIST: `${API_PREFIX}/repairs`,
    GET: (id: number) => `${API_PREFIX}/repairs/${id}`,
    START: (id: number) => `${API_PREFIX}/repairs/${id}/start`,
    RELABEL: (id: number) => `${API_PREFIX}/repairs/${id}/relabel`,
    COMPLETE: (id: number) => `${API_PREFIX}/repairs/${id}/complete`,
    RETURN: (id: number) => `${API_PREFIX}/repairs/${id}/return`,
    CONFIRM_REPAIR: (id: number) =>
      `${API_PREFIX}/repairs/${id}/confirm-repair`,
    BULK_DELETE: `${API_PREFIX}/repairs/bulk-delete`
  },
  REPAIR_DESTINATIONS: {
    PUBLIC: `${API_PREFIX}/repair-destinations/public`,
    UPDATE: (id: number) => `${API_PREFIX}/repair-destinations/${id}`,
    LIST_REASONS: (destId: number) =>
      `${API_PREFIX}/repair-destinations/${destId}/reasons`,
    STORE_REASON: (destId: number) =>
      `${API_PREFIX}/repair-destinations/${destId}/reasons`,
    UPDATE_REASON: (id: number) => `${API_PREFIX}/repair-reasons/${id}`,
    DELETE_REASON: (id: number) => `${API_PREFIX}/repair-reasons/${id}`
  },
  SHELVES: {
    LIST: `${API_PREFIX}/shelves`,
    GET: (id: number) => `${API_PREFIX}/shelves/${id}`,
    CREATE: `${API_PREFIX}/shelves`,
    UPDATE: (id: number) => `${API_PREFIX}/shelves/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/shelves/${id}`,
    STATS: `${API_PREFIX}/shelves/stats`,
    EXPORT: `${API_PREFIX}/shelves/export`,
    CONTAINERS: (id: number) => `${API_PREFIX}/shelves/${id}/containers`
  },
  WAREHOUSE: {
    SCAN_SHELF: `${API_PREFIX}/warehouse/scan-shelf`,
    SCAN_CONTAINER: `${API_PREFIX}/warehouse/scan-container`,
    CONFIRM: `${API_PREFIX}/warehouse/confirm`,
    ASSIGNMENTS: `${API_PREFIX}/warehouse/assignments`,
    RELEASE_ASSIGNMENT: (id: number) =>
      `${API_PREFIX}/warehouse/assignments/${id}/release`,
    TRANSFER_ASSIGNMENT: (id: number) =>
      `${API_PREFIX}/warehouse/assignments/${id}/transfer`
  },
  CLIENTS: {
    LIST: `${API_PREFIX}/clients`,
    GET: (id: number) => `${API_PREFIX}/clients/${id}`,
    CREATE: `${API_PREFIX}/clients`,
    UPDATE: (id: number) => `${API_PREFIX}/clients/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/clients/${id}`,
    METRICS: `${API_PREFIX}/clients/metrics`,
    BULK_DELETE: `${API_PREFIX}/clients/bulk-delete`
  },
  QR_RESOLVE: `${API_PREFIX}/qr/resolve`,
  SEARCH: `${API_PREFIX}/search`,
  PORTAL_AUTH: {
    LOGIN: `${API_PREFIX}/client/auth/login`,
    VERIFY_LOGIN_OTP: `${API_PREFIX}/client/auth/verify-otp`,
    FORGOT_PASSWORD: `${API_PREFIX}/client/auth/forgot-password`,
    RESET_PASSWORD: `${API_PREFIX}/client/auth/reset-password`,
    RESEND_OTP: `${API_PREFIX}/client/auth/resend-otp`,
    LOGOUT: `${API_PREFIX}/client/auth/logout`,
    REFRESH: `${API_PREFIX}/client/auth/refresh`
  },
  PORTAL_DASHBOARD: {
    METRICS: `${API_PREFIX}/client/dashboard`
  },
  PORTAL_PROFILE: {
    GET: `${API_PREFIX}/client/profile`,
    UPDATE: `${API_PREFIX}/client/profile`
  },
  PORTAL_CONTAINERS: {
    LIST: `${API_PREFIX}/client/containers`
  },
  DRIVERS: {
    LIST: `${API_PREFIX}/drivers`,
    GET: (id: number) => `${API_PREFIX}/drivers/${id}`,
    CREATE: `${API_PREFIX}/drivers`,
    UPDATE: (id: number) => `${API_PREFIX}/drivers/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/drivers/${id}`,
    BULK_DELETE: `${API_PREFIX}/drivers/bulk-delete`,
    AVAILABILITY: (id: number) => `${API_PREFIX}/drivers/${id}/availability`
  },
  OCR_TEMPLATES: {
    LIST: `${API_PREFIX}/ocr-templates`,
    GET: (id: number) => `${API_PREFIX}/ocr-templates/${id}`,
    CREATE: `${API_PREFIX}/ocr-templates`,
    UPDATE: (id: number) => `${API_PREFIX}/ocr-templates/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/ocr-templates/${id}`,
    BULK_DELETE: `${API_PREFIX}/ocr-templates/bulk-delete`,
    EXPORT: `${API_PREFIX}/ocr-templates/export`,
    // Bulk import: the blank multi-sheet workbook, and the upload endpoint
    // (accepts that workbook or an equivalent JSON file).
    IMPORT_TEMPLATE: `${API_PREFIX}/ocr-templates/import-template`,
    BULK_IMPORT: `${API_PREFIX}/ocr-templates/bulk-import`,
    GENERATE_DESCRIPTION: `${API_PREFIX}/ocr-templates/generate-description`,
    SYNC_CUSTOM_FIELDS: (id: number) =>
      `${API_PREFIX}/ocr-templates/${id}/custom-fields`,
    SYNC_FIELD_RULES: (id: number) =>
      `${API_PREFIX}/ocr-templates/${id}/field-rules`,
    // OMA-637 — fields a past save removed. Deleting a field in the editor
    // archives it instead of dropping the row, so these are the read behind
    // the "Archived Fields" tab and the one way back into the live field set.
    ARCHIVED_FIELDS: (id: number) =>
      `${API_PREFIX}/ocr-templates/${id}/archived-fields`,
    // OMA-684 — archive one live field at once (the editor no longer waits for Save).
    ARCHIVE_FIELD: (id: number, fieldId: number) =>
      `${API_PREFIX}/ocr-templates/${id}/archived-fields/${fieldId}/archive`,
    RESTORE_ARCHIVED_FIELD: (id: number, fieldId: number) =>
      `${API_PREFIX}/ocr-templates/${id}/archived-fields/${fieldId}/restore`,
    // OMA-671 — the match percentage an archived field suppresses later
    // discoveries at (null = the template's).
    ARCHIVED_FIELD_MATCH_THRESHOLD: (id: number, fieldId: number) =>
      `${API_PREFIX}/ocr-templates/${id}/archived-fields/${fieldId}/match-threshold`,
    // Per-field extraction history (OMA-625): per template field, how often
    // it came back with a value, without one, or not at all. The whole map in
    // one response — unpaginated by design, the row count is the field count.
    FIELD_HISTORY: (id: number) =>
      `${API_PREFIX}/ocr-templates/${id}/field-history`,
    // The supervisor's discovered-field review queue. A pending row is one
    // sighting of a field the free-form pass found; a verdict decides every
    // sighting of that key under the template.
    DISCOVERED_FIELDS: `${API_PREFIX}/ocr-discovered-fields`,
    DISCOVERED_FIELDS_METRICS: `${API_PREFIX}/ocr-discovered-fields/metrics`,
    // The review drill-down: templates that run the pass, then the containers
    // carrying discoveries.
    DISCOVERED_FIELDS_TEMPLATES: `${API_PREFIX}/ocr-discovered-fields/templates`,
    DISCOVERED_FIELDS_CONTAINERS: `${API_PREFIX}/ocr-discovered-fields/containers`,
    // OMA-667 — the sightings as a field list, and one field's files.
    DISCOVERED_FIELDS_GROUPED: `${API_PREFIX}/ocr-discovered-fields/fields`,
    DISCOVERED_FIELD_FILES: (id: number) =>
      `${API_PREFIX}/ocr-discovered-fields/fields/${id}/files`,
    DISCOVERED_FIELD_APPROVE: (id: number) =>
      `${API_PREFIX}/ocr-discovered-fields/${id}/approve`,
    DISCOVERED_FIELD_REJECT: (id: number) =>
      `${API_PREFIX}/ocr-discovered-fields/${id}/reject`,
    DISCOVERED_FIELD_IGNORE: (id: number) =>
      `${API_PREFIX}/ocr-discovered-fields/${id}/ignore`,
    DISCOVERED_FIELD_RESET: (id: number) =>
      `${API_PREFIX}/ocr-discovered-fields/${id}/reset`,
    // Replace the agent-written description with the supervisor's own. A
    // decider write, but not a verdict — a field can be re-described while it
    // is still pending, because reading it is how it gets decided.
    DISCOVERED_FIELD_DESCRIPTION: (id: number) =>
      `${API_PREFIX}/ocr-discovered-fields/${id}/description`,
    DISCOVERED_FIELDS_BULK_DECIDE: `${API_PREFIX}/ocr-discovered-fields/bulk-decide`,
    DISCOVERED_FIELDS_BULK_DELETE: `${API_PREFIX}/ocr-discovered-fields/bulk-delete`
  },
  OCR: {
    CONTAINERS: `${API_PREFIX}/ocr/containers`,
    // Ad-hoc test containers: upload images/PDF and run the real pipeline.
    TEST_CONTAINERS: `${API_PREFIX}/ocr/test-containers`,
    APPEND_TEST_CONTAINER_PAGES: (containerId: number) =>
      `${API_PREFIX}/ocr/test-containers/${containerId}/pages`,
    DELETE_TEST_CONTAINER: (containerId: number) =>
      `${API_PREFIX}/ocr/test-containers/${containerId}`,
    ACTIVE_JOBS: `${API_PREFIX}/ocr/active-jobs`,
    START: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/start`,
    // Pause cancels the in-flight run's queued page batches (pages already
    // extracted are kept); resume re-dispatches only what never extracted.
    PAUSE: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/pause`,
    RESUME: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/resume`,
    // Force-finish a run whose page-batch worker died. Recovery is automatic and
    // in band; this is the operator's "now", for a container visibly stuck on
    // processing.
    RECOVER: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/recover`,
    REFUSE: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/refuse`,
    SUBMIT_TO_QA: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/submit-to-qa`,
    // Who the container IS — label, registration_id, template pool, QA stage
    // — for the header above the results panel. A SIBLING read to
    // CONTAINER_RESULTS, never a widening of it: that payload is a bare
    // OcrResult[] that use-container-results-live patches item by item, so
    // reshaping it breaks live updates.
    CONTAINER: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}`,
    CONTAINER_RESULTS: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/results`,
    // Read-only archive of OCR runs that were superseded when the container
    // returned from repair. Same serialization as CONTAINER_RESULTS.
    CONTAINER_HISTORY: (id: number) =>
      `${API_PREFIX}/ocr/containers/${id}/history`,
    RESULT: (resultId: number) => `${API_PREFIX}/ocr/results/${resultId}`,
    STATUSES: `${API_PREFIX}/ocr/results/status`,
    UPDATE_FIELD: (resultId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/field`,
    // Creates a field the extraction never produced, with its value and its
    // first highlight (OMA-630). CREATE_FIELD_HIGHLIGHT below needs the field
    // to already exist; this is the only route that adds one.
    CREATE_FIELD: (resultId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/fields`,
    // Renames a field — its `label`, never its key (OMA-631). Separate from
    // UPDATE_FIELD because a value correction verifies the field and a rename
    // does not touch what the field says.
    RENAME_FIELD: (resultId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/field/name`,
    UPDATE_CHUNK_BBOX: (resultId: number, chunkId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/chunks/${chunkId}`,
    // Creates the FIRST highlight for a field that has none. Moving an
    // existing one stays on UPDATE_CHUNK_BBOX.
    CREATE_FIELD_HIGHLIGHT: (resultId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/field/highlight`,
    // Unlinks one highlight from a field. A hand-drawn region is deleted; a
    // pipeline-detected one is kept but stops being referenced.
    DELETE_FIELD_HIGHLIGHT: (resultId: number, chunkId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/field/highlight/${chunkId}`,
    ACCEPT_FIELD: (resultId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/field/accept`,
    ACCEPT_ALL: (resultId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/accept`,
    // Bulk-verify one page's fields in a single request — replaces the old
    // one-acceptField-call-per-field loop the page section used to fire.
    ACCEPT_ALL_ON_PAGE: (resultId: number, page: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/pages/${page}/accept`,
    RETRY: (resultId: number) => `${API_PREFIX}/ocr/results/${resultId}/retry`,
    RETRY_FAILED_BATCHES: (resultId: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/retry-failed-batches`,
    // Re-extract a SINGLE page of a QA-rejected run; every other page keeps
    // its extraction, highlights and QA history.
    RERUN_PAGE: (resultId: number, pageNumber: number) =>
      `${API_PREFIX}/ocr/results/${resultId}/pages/${pageNumber}/rerun`,
    // Re-extract only the SELECTED pages of the current run, each with its
    // own template; unselected pages keep their extraction (OMA-674).
    RERUN_PAGES: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/rerun-pages`,
    // Multi-template candidate pool — see features/ocr/components/ocr-template-pool-dialog.tsx
    LIST_REGISTRATION_TEMPLATES: (registrationId: number) =>
      `${API_PREFIX}/registrations/${registrationId}/ocr-templates`,
    SYNC_REGISTRATION_TEMPLATES: (registrationId: number) =>
      `${API_PREFIX}/registrations/${registrationId}/ocr-templates/sync`,
    SUGGEST_TEMPLATES: (registrationId: number) =>
      `${API_PREFIX}/registrations/${registrationId}/ocr-templates/suggest`,
    // Cheap page list for the "Assign per page" MANUAL picker — no model call, so
    // the panel renders instantly. See ocr-per-page-assignment.tsx.
    CONTAINER_PAGES: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/pages`,
    // Per-page template suggestions for the "Assign per page" flow — see
    // features/ocr/components/ocr-per-page-assignment.tsx
    PAGE_TEMPLATE_SUGGESTIONS: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/page-template-suggestions`,
    // One page's on-demand ranking ("Auto" on a single row).
    PAGE_TEMPLATE_SUGGESTION: (containerId: number, pageId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/pages/${pageId}/template-suggestions`,
    REGENERATE_PAGE_TEMPLATE_SUGGESTIONS: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/page-template-suggestions/regenerate`,
    REGENERATE_POOL_SUGGESTIONS: (registrationId: number) =>
      `${API_PREFIX}/registrations/${registrationId}/ocr-templates/suggest/regenerate`,
    // Suggestion-run history (ChatGPT-style list + detail).
    SUGGESTION_RUNS: (registrationId: number) =>
      `${API_PREFIX}/ocr/registrations/${registrationId}/suggestion-runs`,
    CONTAINER_SUGGESTION_RUNS: (containerId: number) =>
      `${API_PREFIX}/ocr/containers/${containerId}/suggestion-runs`,
    SUGGESTION_RUN: (runId: number) =>
      `${API_PREFIX}/ocr/suggestion-runs/${runId}`
  },
  OCR_QA: {
    LIST: (tier: number) => `${API_PREFIX}/ocr-qa/${tier}`,
    METRICS: (tier: number) => `${API_PREFIX}/ocr-qa/${tier}/metrics`,
    SHOW: (tier: number, resultId: number) =>
      `${API_PREFIX}/ocr-qa/${tier}/${resultId}`,
    START: (tier: number, resultId: number) =>
      `${API_PREFIX}/ocr-qa/${tier}/${resultId}/start`,
    SUBMIT_VERDICT: (tier: number, resultId: number) =>
      `${API_PREFIX}/ocr-qa/${tier}/${resultId}/verdict`,
    // Per-PAGE verdict. SUBMIT_VERDICT above acts on a whole OcrResult, which
    // can cover any number of pages — not a page control.
    SUBMIT_PAGE_VERDICT: (tier: number, resultId: number, page: number) =>
      `${API_PREFIX}/ocr-qa/${tier}/${resultId}/pages/${page}/verdict`,
    CONTAINER_RESULTS: (tier: number, containerId: number) =>
      `${API_PREFIX}/ocr-qa/${tier}/containers/${containerId}/results`,
    CONTAINER_VERDICT: (tier: number, containerId: number) =>
      `${API_PREFIX}/ocr-qa/${tier}/containers/${containerId}/verdict`,
    UPDATE_FIELD: (tier: number, resultId: number) =>
      `${API_PREFIX}/ocr-qa/${tier}/results/${resultId}/field`
  },
  OPERATORS: {
    LIST: `${API_PREFIX}/operators`,
    GET: (id: number) => `${API_PREFIX}/operators/${id}`,
    CREATE: `${API_PREFIX}/operators`,
    UPDATE: (id: number) => `${API_PREFIX}/operators/${id}`,
    DELETE: (id: number) => `${API_PREFIX}/operators/${id}`,
    BULK_DELETE: `${API_PREFIX}/operators/bulk-delete`,
    AVAILABILITY: (id: number) => `${API_PREFIX}/operators/${id}/availability`
  },
  TRANSLATIONS: {
    LIST: `${API_PREFIX}/translations`,
    UPDATE: (id: number) => `${API_PREFIX}/translations/${id}`,
    REVERT: (id: number) => `${API_PREFIX}/translations/${id}`,
    BULK_REVERT: `${API_PREFIX}/translations/bulk-revert`,
    SYNC_KEYS: `${API_PREFIX}/translations/sync-keys`,
    NAMESPACES: `${API_PREFIX}/translations/namespaces`,
    BUNDLE: (locale: string) => `${API_PREFIX}/translations/bundle/${locale}`
  }
} as const;

// HTTP Methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE'
} as const;
